import React, { useEffect, useState } from "react";

import styles from "../../../../styles/kml-delivery-drawer.module.scss";
import { Drawer } from "../../../components/Drawer";
import { EnvironmentsAPI } from "../../../../providers/EnvironmentsAPI";
import { EnvironmentStorage } from "../../../tools/EnvironmentStorage";

import { TextFileReader } from "../../../components/TextFileReader";
import * as toGeoJson from "@tmcw/togeojson";
import KMLMap from "./KMLMap";

import { Button, Loading } from "@components";
import { If } from "shared/components/control-statements";
import SearchAddresses from "shared/containers/SearchAddresses";
import SearchAddressesLocation from "shared/containers/SearchAddressLocation";
import { UserStorage } from "shared/tools/UserStorage";

export default function KMLDeliveryDrawer({ visible, onClose }) {
  const [loading, setLoading] = useState(true);
  const [deliveryPolygons, setDeliveryPolygons] = useState([]);
  const [addressOptions, setAddressOptions] = useState([]);
  const [kml, setKml] = useState(null);
  const [openTest, setOpenTest] = useState(false);

  useEffect(() => {
    if (visible) setLoading(true);
  }, [visible]);

  const parseKml = async ({ content }) => {
    const kml = new DOMParser().parseFromString(content, "application/xml");
    const converted = toGeoJson.kml(kml);

    const extractGeometry = ({ properties, geometry }) => {
      if (geometry.type === "Polygon") {
        const polyGeometry = geometry.coordinates[0];
        const points = polyGeometry.map((coord) => [coord[1], coord[0]]);
        return {
          ...properties,
          points,
          type: geometry.type,
        };
      }

      return null;
    };

    const features = converted.features;
    const polyArray = [];

    for (const feature of features) {
      const properties = feature?.properties || {};

      if (["Polygon"].includes(feature.geometry.type)) {
        const extracted = extractGeometry({
          properties: {
            name: properties?.name || "",
            fill: properties?.fill || "",
            "fill-opacity": properties?.["fill-opacity"] || "",
            stroke: properties?.stroke || "",
            "stroke-opacity": properties?.["stroke-opacity"] || "",
            "stroke-width": properties?.["stroke-width"] || "",
            description: properties?.description || "",
            price: Number.parseFloat(properties?.description) || 0,
          },
          geometry: feature.geometry,
        });

        console.log("extracted", extracted);

        if (extracted) polyArray.push(extracted);
      }
    }

    setDeliveryPolygons(polyArray);
  };

  const saveDeliveryLimit = async (data) => {
    setLoading(true);
    try {
      const selectedEnvironment =
        await EnvironmentStorage.getSelectedEnvironment();
      const updateResponse = await EnvironmentsAPI.putConfigurationsByAlias(
        selectedEnvironment,
        "delivery_polygons",
        { delivery_polygons: data || deliveryPolygons }
      );

      const errors = updateResponse.getErrors([]);
      if (errors.length > 0) {
        alert({ text: errors[0], buttonText: "Ok" });

        return false;
      }

      loadKml();

      if (data)
        alert({
          text: "KML removido com sucesso",

          buttonText: "Ok",
        });
      else
        alert({
          text: "KML salvo com sucesso",
          buttonText: "Ok",
        });
    } catch (error) {
      alert({
        text: "Ocorreu um erro ao salvar os limites de entrega.",
        buttonText: "Ok",
      });
    }

    setLoading(false);
  };

  const loadKml = async () => {
    setLoading(true);

    try {
      const kml = await EnvironmentsAPI.getConfigurationsByAlias(
        EnvironmentStorage.getSelectedEnvironment(),
        "delivery_polygons"
      );

      setLoading(false);

      if ((kml || []).length > 0) setKml(kml);
    } catch (error) {
      alert({
        text: "Ocorreu um erro ao carregar os limites de entrega.",
        buttonText: "Ok",
      });
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDeliveryPolygons = async () => {
      try {
        const selectedEnvironment =
          await EnvironmentStorage.getSelectedEnvironment();
        const deliveryPolygons = await EnvironmentsAPI.getConfigurationsByAlias(
          selectedEnvironment,
          "delivery_polygons"
        );

        setDeliveryPolygons(
          Array.isArray(deliveryPolygons) ? deliveryPolygons : []
        );
      } catch (error) {
        alert({
          text: "Ocorreu um erro ao carregar os limites de entrega.",
          buttonText: "Ok",
        });
      }
    };

    const getEnvironment = async () => {
      const environment_id = EnvironmentStorage.getSelectedEnvironment();
      await fetchDeliveryPolygons();

      EnvironmentsAPI.getConfigurationsAddress(environment_id).then(
        (response) => {
          const address = response.getData({});
          setAddressOptions(address);
        }
      );

      loadKml();
    };

    if (visible) {
      getEnvironment();
    }
  }, [visible]);

  return (
    <Drawer
      visible={visible}
      onClose={onClose}
      withoutTransition
      withoutScroll
      direction="full-center"
      showCloseButton={false}
    >
      <div className={styles.header}>
        <p className={styles.title}>Limites de entrega (KML)</p>
        <div className="d-flex align-center justify-end flex-1">
          {kml && UserStorage.isGodMode && (
            <Button
              onClick={async () => {
                const confirmed = await window.confirm({
                  text: "Tem certeza que deseja remover seu arquivo KML?",
                  confirmText: "Sim, remover",
                  cancelText: "Cancelar",
                });

                if (!confirmed) return false;

                await saveDeliveryLimit([]);
                setKml(null);
              }}
            >
              Remover KML
            </Button>
          )}
          {kml && (
            <Button className="ml-10" onClick={() => setOpenTest(true)}>
              Testar localização
            </Button>
          )}
          <div className={styles.close} onClick={onClose}>
            <i className="fas fa-times" />
          </div>
        </div>
      </div>
      <div className="h-100" id="oracle-flow">
        <div className={styles.container}>
          <If cond={loading}>
            <div className={styles.loading}>
              <Loading />
            </div>
          </If>

          <If cond={!loading}>
            {kml && <KMLMap addressOptions={addressOptions} kml={kml} />}

            {!kml && UserStorage.isGodMode && (
              <div className="h-100 ml-20 mr-20 d-flex flex-column align-center justify-center">
                <TextFileReader
                  allowedFiles={"text/kml"}
                  label="Importar novo arquivo KML"
                  onFileLoaded={({ name, content }) =>
                    parseKml({ name, content })
                  }
                  loading={loading}
                  saveDeliveryLimit={() => saveDeliveryLimit()}
                />
              </div>
            )}
          </If>
        </div>
      </div>

      <Drawer
        visible={openTest}
        onClose={() => setOpenTest(false)}
        direction="center-right"
        title="Testar endereço"
        subtitle="Informe um endereço para visualizar no mapa"
        parentSelector={() => document.getElementById("oracle-flow")}
      >
        <SearchAddressesLocation
          addressOptions={addressOptions}
          deliveryPolygons={deliveryPolygons}
          setSelectedAddress={(address) => {
            if (address) {
              setAddressOptions(address);
              setOpenTest(false);
            }
          }}
        />
      </Drawer>
    </Drawer>
  );
}
